import React, { Component } from 'react';
import AthleticColorChart from './AthleticColorChart';
import { parseSvgColors } from './parseSvgColors';

const styles = {
    root: {
        display: 'flex',
        flexDirection: 'column',
        gap: '20px',
    },
    sectionLabel: {
        fontSize: '11px',
        color: '#888',
        textTransform: 'uppercase',
        letterSpacing: '1px',
        marginBottom: '8px',
    },
    stripeGrid: {
        display: 'grid',
        gridTemplateColumns: 'repeat(3, 1fr)',
        gap: '6px',
    },
    stripeBtn: (isActive) => ({
        padding: '6px 4px',
        background: isActive ? '#2a2a2a' : '#1a1a1a',
        border: isActive ? '2px solid #ecff19' : '2px solid #333',
        borderRadius: '6px',
        color: isActive ? '#ecff19' : '#aaa',
        fontSize: '11px',
        cursor: 'pointer',
        textAlign: 'center',
        transition: 'border-color 0.15s, color 0.15s',
        position: 'relative',
    }),
    checkMark: {
        position: 'absolute',
        top: '3px',
        right: '4px',
        fontSize: '8px',
        color: '#ecff19',
        lineHeight: 1,
    },
    stripeTabRow: {
        display: 'flex',
        gap: '6px',
        flexWrap: 'wrap',
        marginBottom: '10px',
    },
    stripeTab: (isActive) => ({
        padding: '3px 8px',
        background: isActive ? '#2a2a2a' : '#1a1a1a',
        border: isActive ? '2px solid #ecff19' : '2px solid #333',
        borderRadius: '4px',
        color: isActive ? '#ecff19' : '#888',
        fontSize: '10px',
        cursor: 'pointer',
        transition: 'border-color 0.15s, color 0.15s',
    }),
    slotTabRow: {
        display: 'flex',
        gap: '8px',
        marginBottom: '10px',
    },
    slotTab: (color, isActive) => ({
        width: '32px',
        height: '32px',
        borderRadius: '50%',
        backgroundColor: color,
        border: isActive ? '3px solid #ecff19' : '3px solid transparent',
        outline: isActive ? '1px solid #555' : '1px solid #333',
        boxSizing: 'border-box',
        cursor: 'pointer',
        flexShrink: 0,
        transition: 'border-color 0.15s',
    }),
    slotLabel: {
        fontSize: '10px',
        color: '#666',
        textAlign: 'center',
        marginTop: '2px',
    },
    slotWrap: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: '2px',
    },
    divider: {
        borderTop: '1px solid #2a2a2a',
        margin: '4px 0 10px',
    },
    noStripes: {
        fontSize: '11px',
        color: '#555',
        fontStyle: 'italic',
    },
};

const patternBtnStyle = (isActive) => ({
    padding: '7px 4px',
    background: isActive ? '#2a2a2a' : '#1a1a1a',
    border: isActive ? '2px solid #ecff19' : '2px solid #333',
    borderRadius: '6px',
    color: isActive ? '#ecff19' : '#aaa',
    fontSize: '10px',
    fontWeight: isActive ? '600' : '400',
    cursor: 'pointer',
    textAlign: 'center',
    transition: 'border-color 0.15s, color 0.15s',
});

/**
 * OptionPicker
 *
 * Generic garment-option picker (replaces the old shorts-specific StripePicker).
 *
 * Props:
 *   options                    – [{ id, label, url, colorSlots: [{ originalHex }] }]  from API
 *   patterns                   – [{ id, label, url }]  available fill patterns from API
 *   selectedStripeIds          – string[]
 *   stripeColors               – { [stripeId]: string[] }  solid colors per stripe
 *   stripePatterns             – { [stripeId]: patternId | null }
 *   stripePatternColors        – { [stripeId]: string[] }  pattern slot colors per stripe
 *   stripePatternOriginals     – { [stripeId]: { [patternId]: ColorSlot[] } }
 *   onStripeToggle             – (stripeId) => void
 *   onStripeColorChange        – (stripeId, slotIndex, color) => void
 *   onStripePatternChange      – (stripeId, patternId | null) => void
 *   onStripePatternColorChange – (stripeId, slotIndex, color) => void
 *   onStripePatternColorsDiscovered – (stripeId, patternId, slots) => void
 */
class OptionPicker extends Component {
    constructor(props) {
        super(props);
        this.state = {
            editingStripeId: null,
            activeSlot: 0,
            // cache of fetched pattern SVG texts, keyed by patternId
            patternSvgTexts: {},
        };
    }

    componentDidMount() {
        // Pre-fetch all pattern SVGs so we can parse their color slots when needed
        const { patterns = [] } = this.props;
        patterns.forEach((p) => {
            fetch(p.url)
                .then((r) => r.text())
                .then((text) => {
                    this.setState((prev) => ({
                        patternSvgTexts: { ...prev.patternSvgTexts, [p.id]: text },
                    }));
                });
        });
    }

    componentDidUpdate(prevProps) {
        const { selectedStripeIds, patterns = [] } = this.props;
        const { editingStripeId } = this.state;

        // Fetch newly added patterns
        if (prevProps.patterns !== patterns) {
            const prevIds = new Set((prevProps.patterns || []).map((p) => p.id));
            patterns.forEach((p) => {
                if (prevIds.has(p.id)) return;
                fetch(p.url)
                    .then((r) => r.text())
                    .then((text) => {
                        this.setState((prev) => ({
                            patternSvgTexts: { ...prev.patternSvgTexts, [p.id]: text },
                        }));
                    });
            });
        }

        // If the stripe being edited was just deselected, move focus to
        // the first remaining active stripe (or null if none)
        if (editingStripeId && !selectedStripeIds.includes(editingStripeId)) {
            this.setState({
                editingStripeId: selectedStripeIds[0] || null,
                activeSlot: 0,
            });
        }

        // Auto-select the first newly-added stripe for editing
        if (selectedStripeIds.length > prevProps.selectedStripeIds.length) {
            const newId = selectedStripeIds.find(
                (id) => !prevProps.selectedStripeIds.includes(id)
            );
            if (newId) this.setState({ editingStripeId: newId, activeSlot: 0 });
        }
    }

    // Called when a pattern is selected for a stripe — parse its colors and notify parent
    _discoverPatternColors(stripeId, patternId) {
        const { onStripePatternColorsDiscovered, stripePatternOriginals } = this.props;
        const { patternSvgTexts } = this.state;
        // Skip if already discovered
        if (
            stripePatternOriginals &&
            stripePatternOriginals[stripeId] &&
            stripePatternOriginals[stripeId][patternId]
        ) return;
        const text = patternSvgTexts[patternId];
        if (!text || !onStripePatternColorsDiscovered) return;
        const slots = parseSvgColors(text);
        onStripePatternColorsDiscovered(stripeId, patternId, slots);
    }

    render() {
        const {
            options = [],
            patterns = [],
            selectedStripeIds,
            stripeColors,
            stripePatterns = {},
            stripePatternColors = {},
            stripePatternOriginals = {},
            onStripeToggle,
            onStripeColorChange,
            onStripePatternChange,
            onStripePatternColorChange,
        } = this.props;
        const { editingStripeId, activeSlot, patternSvgTexts } = this.state;

        // Fill type options: Solid + all patterns from props
        const fillTypeOptions = [
            { id: null, label: 'Solid' },
            ...patterns,
        ];

        const editingOption = options.find((s) => s.id === editingStripeId) || null;

        // Which fill mode is active for the option being edited
        const editingPatternId = editingStripeId ? (stripePatterns[editingStripeId] ?? null) : null;
        const isPatternFill = editingPatternId !== null;

        // Derive colorCount from the option's colorSlots array
        const colorCount = editingOption ? (editingOption.colorSlots || []).length : 0;
        const originals = editingOption ? (editingOption.colorSlots || []).map((s) => s.originalHex) : [];

        const clampedSlot = colorCount > 0 ? Math.min(activeSlot, colorCount - 1) : 0;
        const solidColors = editingStripeId
            ? (stripeColors[editingStripeId] || originals)
            : [];

        const patternSlots = (
            editingStripeId && editingPatternId &&
            stripePatternOriginals[editingStripeId] &&
            stripePatternOriginals[editingStripeId][editingPatternId]
        ) ? stripePatternOriginals[editingStripeId][editingPatternId] : null;

        const patternColors = editingStripeId
            ? (stripePatternColors[editingStripeId] || [])
            : [];

        const patternColorCount = patternSlots ? patternSlots.length : 0;
        const clampedPatternSlot = Math.min(activeSlot, Math.max(0, patternColorCount - 1));

        return (
            <div style={styles.root}>

                {/* ── Option toggles (checkbox behaviour) ── */}
                <div>
                    <div style={styles.sectionLabel}>Options — select any</div>
                    <div style={styles.stripeGrid}>
                        {options.map((s) => {
                            const isActive = selectedStripeIds.includes(s.id);
                            return (
                                <button
                                    key={s.id}
                                    style={styles.stripeBtn(isActive)}
                                    onClick={() => onStripeToggle(s.id)}
                                    title={isActive ? `Remove ${s.label}` : `Add ${s.label}`}
                                >
                                    {isActive && <span style={styles.checkMark}>✓</span>}
                                    {s.label}
                                </button>
                            );
                        })}
                    </div>
                </div>

                {/* ── Color editing (only when at least one option is active) ── */}
                {selectedStripeIds.length === 0 ? (
                    <div style={styles.noStripes}>No options selected.</div>
                ) : (
                    <div>
                        {/* option selector tabs — pick which active option to edit */}
                        {selectedStripeIds.length > 1 && (
                            <div>
                                <div style={styles.sectionLabel}>Edit Fill For</div>
                                <div style={styles.stripeTabRow}>
                                    {selectedStripeIds.map((id) => {
                                        const opt = options.find((s) => s.id === id);
                                        return (
                                            <button
                                                key={id}
                                                style={styles.stripeTab(editingStripeId === id)}
                                                onClick={() => this.setState({ editingStripeId: id, activeSlot: 0 })}
                                            >
                                                {opt ? opt.label : id}
                                            </button>
                                        );
                                    })}
                                </div>
                            </div>
                        )}

                        {editingOption && (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>

                                {/* ── Fill type: Solid vs Pattern ── */}
                                <div>
                                    <div style={styles.sectionLabel}>Fill Type</div>
                                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '5px' }}>
                                        {fillTypeOptions.map((opt) => {
                                            const isActive = editingPatternId === opt.id;
                                            const canShow = opt.id === null || !!patternSvgTexts[opt.id];
                                            return (
                                                <button
                                                    key={String(opt.id)}
                                                    style={patternBtnStyle(isActive)}
                                                    disabled={!canShow}
                                                    onClick={() => {
                                                        if (onStripePatternChange) {
                                                            onStripePatternChange(editingStripeId, opt.id);
                                                            this.setState({ activeSlot: 0 });
                                                            if (opt.id !== null) {
                                                                this._discoverPatternColors(editingStripeId, opt.id);
                                                            }
                                                        }
                                                    }}
                                                >
                                                    {opt.label}
                                                </button>
                                            );
                                        })}
                                    </div>
                                </div>

                                <div style={styles.divider} />

                                {/* ── Solid fill controls ── */}
                                {!isPatternFill && (
                                    <div>
                                        <div style={styles.sectionLabel}>
                                            {`Color${colorCount > 1 ? ` ${clampedSlot + 1} of ${colorCount}` : ''}`}
                                        </div>
                                        {colorCount > 1 && (
                                            <div style={styles.slotTabRow}>
                                                {Array.from({ length: colorCount }, (_, i) => (
                                                    <div key={i} style={styles.slotWrap}>
                                                        <div
                                                            style={styles.slotTab(solidColors[i] || '#888', clampedSlot === i)}
                                                            onClick={() => this.setState({ activeSlot: i })}
                                                            title={`Color ${i + 1}`}
                                                        />
                                                        <span style={styles.slotLabel}>{i + 1}</span>
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                        <AthleticColorChart
                                            selectedColor={solidColors[clampedSlot] || originals[clampedSlot]}
                                            onSelect={(hex) => onStripeColorChange(editingStripeId, clampedSlot, hex)}
                                        />
                                    </div>
                                )}

                                {/* ── Pattern fill controls ── */}
                                {isPatternFill && (
                                    <div>
                                        {patternSlots === null ? (
                                            <div style={{ fontSize: '11px', color: '#555', fontStyle: 'italic' }}>
                                                Loading pattern colors…
                                            </div>
                                        ) : (
                                            <div>
                                                <div style={styles.sectionLabel}>
                                                    {`Pattern Color${patternColorCount > 1 ? ` ${clampedPatternSlot + 1} of ${patternColorCount}` : ''}`}
                                                </div>
                                                {patternColorCount > 1 && (
                                                    <div style={styles.slotTabRow}>
                                                        {patternSlots.map((slot, i) => {
                                                            const c = patternColors[i] !== undefined ? patternColors[i] : slot.color;
                                                            return (
                                                                <div key={i} style={styles.slotWrap}>
                                                                    <div
                                                                        style={styles.slotTab(c, clampedPatternSlot === i)}
                                                                        onClick={() => this.setState({ activeSlot: i })}
                                                                        title={`Color ${i + 1}`}
                                                                    />
                                                                    <span style={styles.slotLabel}>{i + 1}</span>
                                                                </div>
                                                            );
                                                        })}
                                                    </div>
                                                )}
                                                <AthleticColorChart
                                                    selectedColor={
                                                        patternColors[clampedPatternSlot] !== undefined
                                                            ? patternColors[clampedPatternSlot]
                                                            : (patternSlots[clampedPatternSlot] ? patternSlots[clampedPatternSlot].color : '#888')
                                                    }
                                                    onSelect={(hex) => onStripePatternColorChange(editingStripeId, clampedPatternSlot, hex)}
                                                />
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                )}

            </div>
        );
    }
}

export default OptionPicker;
